import { useState, useCallback, useEffect } from "react";

const FALLBACK = { lat: 50.3714, lng: -4.1425 };

export function useGeolocation() {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFallback, setIsFallback] = useState(false);
  const [denied, setDenied] = useState(false); // true when user explicitly denied

  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Your browser does not support location.");
      setIsFallback(true);
      setLocation(FALLBACK);
      return;
    }
    setLoading(true);
    setError(null);
    setDenied(false);

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocation({ lat: coords.latitude, lng: coords.longitude });
        setIsFallback(false);
        setDenied(false);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        setIsFallback(true);
        setLocation(FALLBACK);
        if (err.code === 1) {
          // PERMISSION_DENIED
          setDenied(true);
          setError("permission_denied");
        } else {
          setError("Could not get your location. Please try again.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  // On mount: if permission is already granted, fetch silently (no popup needed).
  // Do NOT auto-prompt — browsers block automatic geolocation without a user
  // gesture. The App shows a custom popup on first load instead.
  useEffect(() => {
    let cancelled = false;
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (!cancelled && result.state === "granted") {
          setLoading(true);
          navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
              if (!cancelled) { setLocation({ lat: coords.latitude, lng: coords.longitude }); setLoading(false); }
            },
            () => { if (!cancelled) setLoading(false); },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        }
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, []);

  return { location, error, loading, request, isFallback, denied };
}
